/**
 * Dependency Injection Setup for Admin App
 * 
 * Configures client-side dependencies like API client, auth, etc.
 */
import { DIContainer } from './container';
import { QueryClient } from '@tanstack/react-query';
import { createApiClient, type ApiClient } from '@/lib/api-client';

export interface AdminDIConfig {
  apiUrl: string;
  supabaseUrl: string;
  supabaseAnonKey: string;
}

/**
 * Create and configure the DI container for admin app
 */
export function createContainer(config: AdminDIConfig): DIContainer {
  const container = new DIContainer();

  // Register React Query client (singleton)
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 1000 * 60 * 5, // 5 minutes
        refetchOnWindowFocus: false,
        retry: (failureCount, error: any) => {
          // Don't retry on 401 errors
          if (error?.status === 401) {
            return false;
          }
          // Retry up to 2 times for other errors
          return failureCount < 2;
        },
      },
    },
  });
  container.registerSingleton('queryClient', queryClient);

  // Register API client factory (with authentication)
  const apiClient = createApiClient(config.apiUrl);
  container.registerSingleton('apiClient', apiClient);

  // Register Supabase client factory (for auth)
  container.register('supabaseClient', () => {
    // We'll use @supabase/supabase-js client-side
    // This is a placeholder - actual implementation will be added later
    return {
      url: config.supabaseUrl,
      anonKey: config.supabaseAnonKey,
    };
  }, true);

  return container;
}

export type { QueryClient };

